import { useMount } from '@tiger/hooks';
import Icon from '@tiger/icons';
import lodash from 'lodash';
import { FC, TouchEvent, useEffect, useRef, useState } from 'react';
import { ModalHandler } from 'components/base/Modal';
import HashLink from 'components/HashLink';
import type { CountryData } from 'types/common';
import classlist from 'utils/classlist';
import styles from './style.scss';

interface PortalMobileProps extends ModalHandler {
    countryData: CountryData[];
    countryCode?: string;
}

const PortalMobile: FC<PortalMobileProps> = ({ countryData, countryCode, close, dismiss }) => {
    const [curLetter, setCurLetter] = useState(() =>
        countryData.length > 0
            ? countryData.find(countryItem => countryItem.items.some(item => item.country_code === countryCode))
                  ?.letter || countryData[0].letter
            : ''
    );
    const sectionRef = useRef<HTMLDivElement>(null);
    const letterListsRef = useRef<HTMLDivElement>(null);
    const letterItemHeightRef = useRef<number>(0); // 每个菜单选项的高度
    const letterRoundHeightRef = useRef<number>(0); // 菜单取整的高度
    const letterRectTopRef = useRef<number>(0);
    const countryListMapRef = useRef<Array<{ letter: string; territory: number }>>([]);
    const isTouchMoveRef = useRef<boolean>(false);

    const initMenuList = () => {
        const { current: letterLists } = letterListsRef;

        if (letterLists && countryData.length > 0) {
            // 因外层slide动画原因，所获取 rect 处于变动过程中的值，需等待外层动画执行完成
            window.setTimeout(() => {
                const rect = letterLists.getBoundingClientRect();
                const letterItemHeight = (document.querySelector(`.${styles.letterItem}`)?.clientHeight || 18) + 1;
                const letterRoundHeight = rect.height - (rect.height % letterItemHeight);

                letterItemHeightRef.current = letterItemHeight;
                letterRoundHeightRef.current = letterRoundHeight;
            }, 10);
        }
    };

    const initCountryList = () => {
        if (sectionRef.current) {
            // 因外层slide动画原因，所获取 rect 处于变动过程中的值，需等待外层动画执行完成
            window.setTimeout(() => {
                let territory = 0;

                countryListMapRef.current = Array.from(sectionRef.current!.children).map((item: HTMLDivElement) => {
                    // 计算改 item 底部临界线
                    territory += item.clientHeight + parseInt(getComputedStyle(item).marginTop);

                    return { letter: item.getAttribute('data-letter') || '', territory };
                });
            }, 10);
        }
    };

    // 定位到current country
    const countryAutoPosition = (key: string) => {
        const listDom = sectionRef.current;

        if (listDom) {
            const elem = listDom.querySelector<HTMLDivElement>(`.${styles.item}[data-code="${key}"]`);

            if (elem) {
                window.setTimeout(() => {
                    const marginTop = parseInt(getComputedStyle(listDom).marginTop);

                    // 滚动条高度 = 元素的 offsetTop 减去2 个元素高度 (元素本身和标题高度) 减去 容器外边距
                    listDom.scrollTop = elem.offsetTop - elem.clientHeight * 2 - marginTop;
                });
            }
        }
    };

    // 在导航没有触发 touchMove 时的定位
    const letterAutoPosition = () => {
        if (letterListsRef.current) {
            const letterActive = letterListsRef.current.querySelector<HTMLAnchorElement>(
                `.${styles.letterItem}.${styles.active}`
            );

            if (letterActive) {
                const top = letterActive.offsetTop;

                letterListsRef.current.scrollTop = top > letterListsRef.current.clientHeight / 3 ? top : 0;
            }
        }
    };

    // 国家列表滚动到对应 letter 位置
    const scrollTo = (letter: string) => {
        const elem = document.getElementById(`countrycode-letter-${letter}`);

        if (elem) {
            elem.scrollIntoView();

            setCurLetter(letter);
        }
    };

    // 单词点击
    const handleLetterClick = (letter: string) => {
        scrollTo(letter);
    };

    // 菜单滑动选中
    const handleLetterTouchMove = (event?: TouchEvent) => {
        if (event && letterListsRef.current) {
            event.stopPropagation();

            isTouchMoveRef.current = true;

            const pageY = event.touches[0].pageY;
            const letterRectTop =
                letterRectTopRef.current ||
                (letterRectTopRef.current = letterListsRef.current!.getBoundingClientRect().top);
            const offsetTop = pageY - letterRectTop + letterListsRef.current.scrollTop;
            const index = Math.floor(offsetTop / letterItemHeightRef.current);

            if (index >= 0 && index <= countryData.length - 1) {
                const letter = countryData[index].letter;
                const maxHeight = letterRoundHeightRef.current - letterItemHeightRef.current / 2;

                if (offsetTop - letterListsRef.current.scrollTop > maxHeight) {
                    letterListsRef.current.scrollTop =
                        Math.ceil((offsetTop - maxHeight) / letterItemHeightRef.current) * letterItemHeightRef.current;
                } else if (offsetTop - letterListsRef.current.scrollTop <= letterItemHeightRef.current) {
                    letterListsRef.current.scrollTop -= letterItemHeightRef.current;
                }

                scrollTo(letter);
            }
        }
    };

    const handleLetterTouchEnd = () => {
        isTouchMoveRef.current = false;
    };

    useMount(() => {
        // 监听滚动事件，调整被选中的国家
        const handleScroll = (event?: Event) => {
            if (event) {
                event.preventDefault();

                const target = event.target as HTMLDivElement;
                const scrollTop = target.scrollTop;
                // 计算滚动条是否小于某 item 底部临界线，并获得该 item 的 letter
                const letter = countryListMapRef.current.find(item => scrollTop < item.territory)?.letter;

                letter && setCurLetter(letter);
            }
        };

        initMenuList();
        initCountryList();
        countryCode && countryAutoPosition(countryCode);

        const handleScrollDebounce = lodash.throttle(handleScroll, 100);

        sectionRef.current?.addEventListener('scroll', handleScrollDebounce);

        return () => {
            sectionRef.current?.removeEventListener('scroll', handleScrollDebounce);
        };
    });

    useEffect(() => {
        if (curLetter && !isTouchMoveRef.current) {
            letterAutoPosition();
        }
    }, [curLetter]);

    return (
        <div className={styles.mobileRoot}>
            <div className={styles.select}>
                <div
                    className={styles.letterList}
                    ref={letterListsRef}
                    onTouchEnd={handleLetterTouchEnd}
                    onTouchMove={handleLetterTouchMove}>
                    {countryData.map(({ letter }) => (
                        <HashLink
                            key={letter}
                            className={classlist(styles.letterItem, {
                                [styles.active]: curLetter === letter
                            })}
                            onClick={() => handleLetterClick(letter)}>
                            {letter}
                        </HashLink>
                    ))}
                </div>
                <header className={styles.selectHeader}>
                    <span>{__('选择国家/地区')}</span>
                    <Icon type="close" onClick={() => dismiss()} />
                </header>
                <section className={styles.list} ref={sectionRef}>
                    {countryData.map(({ letter, items }) => (
                        <div
                            key={letter}
                            id={`countrycode-letter-${letter}`}
                            data-letter={letter}
                            className={styles.listItems}>
                            <HashLink className={styles.title} onClick={() => handleLetterClick(letter)}>
                                {letter}
                            </HashLink>
                            <>
                                {items.map(({ country_code, country_name, country }) => {
                                    const isSelected = countryCode === country_code;

                                    return (
                                        <div
                                            key={country_code}
                                            className={classlist(styles.item, {
                                                [styles.current]: isSelected
                                            })}
                                            data-code={country_code}
                                            onClick={() => close(country_code)}>
                                            {country_name || country}
                                            {isSelected && <Icon type="check" fill="#447EFF" />}
                                        </div>
                                    );
                                })}
                            </>
                        </div>
                    ))}
                </section>
            </div>
        </div>
    );
};

export default PortalMobile;
